import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import {
    setTemplate,
    updateTemplateField,
    resetTemplate,
    setSubmitting,
    setShowRegistrationModal,
    closeRegistrationModalAndReset,
    clearForm,
    setList,
    setHtmlPreview,
    setShowEditModal,
    updateTemplateStatus,
} from '../../store/slices/documentTemplate/electronicDocumentTemplateSlice';
import { ElectronicDocumentTemplateForm } from '../../types/documentTemplate/ElectronicDocumentTemplateTypes';
import ElectronicDocumentTemplateService from '../../services/documentTemplate/ElectronicDocumentTemplateService';
import useApiErrorHandler from '../useApiErrorHandler';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import useModals from '../useModals';

export const useDocumentTemplate = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { openModal } = useModals();

    const {
        form,
        isSubmitting,
        list,
        paginator,
        error,
        showRegistrationModal,
        showEditModal,
        showPreviewModal,
        htmlPreview,
    } = useSelector(
        (state: RootState) => state.electronicDocumentTemplate
    );

    const { handle } = useApiErrorHandler();
    const { show: showAlert } = useAlerts();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();


    const loadHtmlPreview = async (id: number) => {
        try {
            showLoadingIndicator();
            const blob = await ElectronicDocumentTemplateService.previewTemplateById(id);

            const reader = new FileReader();
            reader.onload = () => {
                dispatch(setHtmlPreview(reader.result as string));
            };
            reader.readAsText(blob);
        } catch (error) {
            showAlert('error', 'Error', 'No se pudo cargar la vista previa.');
        } finally {
            hideLoadingIndicator();
        }
    };

    const clearHtmlPreview = () => {
        dispatch(setHtmlPreview(''));
    };

    const handlerGetAllTemplates = async () => {
        try {
            showLoadingIndicator();
            const response = await ElectronicDocumentTemplateService.getAllTemplates();
            const { statusCode, title, message, type, data } = response;

            if (statusCode === 200) {
                dispatch(setList(data));
                showAlert(type, title, message);
            }
        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    const fetchTemplateById = async (id: number, mode: 'edit' | 'preview') => {
        try {
            showLoadingIndicator();

            const response = await ElectronicDocumentTemplateService.getTemplateForEdit(id);
            const { data } = response;

            dispatch(setTemplate(data));

            if (mode === 'edit') {
                dispatch(setShowEditModal(true));
            }

            // si en un futuro quieres usar modo preview, también puedes manejarlo aquí

        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };


    const saveTemplate = async (form: ElectronicDocumentTemplateForm, file?: File) => {
        try {
            dispatch(setSubmitting(true));
            showLoadingIndicator();

            const payload = { ...form };
            // console.log(payload);

            let response;
            if (form.id) {
                response = await ElectronicDocumentTemplateService.updateTemplate(form.id, payload, file);
            } else {
                response = await ElectronicDocumentTemplateService.createTemplate(payload, file);
            }

            const { statusCode, title, message, type } = response;

            if (statusCode === 200 || statusCode === 201) {
                openModal(type, { title, message });

                dispatch(clearForm()); // 🧹 limpia el formulario del slice

                // ✅ cerrar el modal correspondiente
                if (form.id) {
                    dispatch(setShowEditModal(false));
                } else {
                    dispatch(setShowRegistrationModal(false));
                }

                await handlerGetAllTemplates();
            }
        } catch (error: any) {
            handle(error);
        } finally {
            dispatch(setSubmitting(false));
            hideLoadingIndicator();
        }
    };

    const toggleStatus = async (id: number) => {
        try {
            const response = await ElectronicDocumentTemplateService.toggleStatus(id);
            console.log(response);

            if (response.statusCode === 200) {
                const { id: updatedId, active } = response.data;

                showAlert(response.type, response.title, response.message);

                // ✅ Actualiza solo el campo active en la lista
                dispatch(updateTemplateStatus({ id: updatedId, active }));
            } else {
                showAlert('error', 'Error', 'No se pudo cambiar el estado.');
            }
        } catch (error: any) {
            handle(error);
        }
    };


    const previewTemplateById = async (id: number) => {
        try {
            showLoadingIndicator();
            await ElectronicDocumentTemplateService.previewTemplateById(id);
        } catch (error) {
            showAlert('error', 'Error', 'No se pudo cargar la vista previa de la plantilla.');
        } finally {
            hideLoadingIndicator();
        }
    };



    const updateField = <K extends keyof ElectronicDocumentTemplateForm>(
        field: K,
        value: ElectronicDocumentTemplateForm[K]
    ) => {
        dispatch(updateTemplateField({ field, value }));
    };

    const resetForm = () => {
        dispatch(resetTemplate());
    };

    const openRegistrationModal = () => {
        dispatch(setShowRegistrationModal(true))
    }

    const closeRegistrationModal = () => {
        dispatch(closeRegistrationModalAndReset())
    }

    return {
        showRegistrationModal,
        showEditModal,
        showPreviewModal,
        form,
        isSubmitting,
        list,
        paginator,
        error,
        updateField,
        resetForm,
        fetchTemplateById,
        saveTemplate,
        toggleStatus,

        handlerGetAllTemplates,
        openRegistrationModal,
        closeRegistrationModal,
        previewTemplateById,

        clearHtmlPreview,
        htmlPreview,
        loadHtmlPreview,
    };
};
